import { useEffect, useRef } from "react";
import { Platform, useWindowDimensions } from "react-native";
import { CustomerIO, CioPushPermissionStatus } from "customerio-reactnative";
import Svg, { Path } from "react-native-svg";
import LBox from "@components/LBox";
import LText from "@components/LText";
import { fonts } from "@theme";
import { useCompleteSetup } from "@hooks/trpc/useOnboardingMutations";
import { useSavePushNotificationToken } from "@utils/notifications";
import { router } from "expo-router";
import { trackOnb } from "./analytics/trackOnboarding";
import { useOnboardingStore } from "./store/onboardingStore";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { capabilities } from "../../capabilities";

const BLUE = "#2979FF";

const CurvedArrow = () => (
  <Svg width={64} height={82} viewBox="0 0 64 82" fill="none">
    <Path
      d="M48 72C48 56 43 42 34 28C28 20 20 14 12 10"
      stroke="rgba(41, 121, 255, 0.2)"
      strokeWidth={7}
      strokeLinecap="round"
      fill="none"
    />
    <Path
      d="M48 72C48 56 43 42 34 28C28 20 20 14 12 10"
      stroke={BLUE}
      strokeWidth={3}
      strokeLinecap="round"
      fill="none"
    />
    <Path
      d="M23 8L12 10L15 21"
      stroke={BLUE}
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </Svg>
);

export const NotificationScreen = () => {
  const safeAreaInsets = useSafeAreaInsets();
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();
  const { mutateAsync: completeSetup } = useCompleteSetup();
  useSavePushNotificationToken();
  const hasRun = useRef(false);
  const hasImportedSongs = useOnboardingStore((s) => s.hasImportedSongs);

  const arrowBottom = Math.max(
    safeAreaInsets.bottom + 190,
    screenHeight * 0.24,
  );
  const arrowLeft = screenWidth * 0.55;

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    trackOnb("signup", "notification", "view");

    const run = async () => {
      // Web and previews skip the Customer.io-backed notification prompt.
      if (Platform.OS === "web" || !capabilities.allowed("customerio")) {
        trackOnb("signup", "notification", "skipped", {
          reason: Platform.OS === "web" ? "web" : "customerio_disabled",
        });
        await completeSetup({ method: hasImportedSongs ? "import" : "genre" });
        router.replace("/");
        return;
      }

      // Native: check if permission already determined
      const currentStatus =
        await CustomerIO.pushMessaging.getPushPermissionStatus();

      if (currentStatus !== CioPushPermissionStatus.NotDetermined) {
        // Permission already granted or denied — skip prompt
        trackOnb("signup", "notification", "skipped", {
          reason: "already_determined",
          status: currentStatus,
        });
        await completeSetup({ method: hasImportedSongs ? "import" : "genre" });
        router.replace("/");
        return;
      }

      // Show native permission prompt on page load
      const newStatus =
        await CustomerIO.pushMessaging.showPromptForPushNotifications({
          ios: { badge: true, sound: true },
        });

      trackOnb("signup", "notification", "prompted", {
        result: newStatus,
      });

      // Allow or deny both advance
      await completeSetup({ method: hasImportedSongs ? "import" : "genre" });
      router.replace("/");
    };

    run().catch((error) => {
      console.error("::NotificationScreen:: error:", error);
      // Still advance on error to not block the user
      completeSetup({ method: hasImportedSongs ? "import" : "genre" })
        .then(() => router.replace("/"))
        .catch(console.error);
    });
  }, [completeSetup, hasImportedSongs]);

  return (
    <LBox testID="notification" flex={1} backgroundColor="black">
      {/* Title + subtitle — positioned in upper area */}
      <LBox style={{ paddingTop: "20%" }} alignItems="center" px="m1">
        <LText
          fontSize={28}
          fontFamily={fonts.bold}
          color="white"
          textAlign="center"
          style={{ letterSpacing: -0.56 }}
          lineHeight={34}
        >
          {"Allow Lissen to send\nyou notifications"}
        </LText>
        <LBox mt="s3">
          <LText
            fontSize={14}
            fontFamily={fonts.medium}
            color="whiteTranslucent50"
            textAlign="center"
            lineHeight={20}
          >
            {
              "We will occasionally send you great music\nrecommendations and perks."
            }
          </LText>
        </LBox>
      </LBox>

      {/* Curved arrow pointing up at the native dialog */}
      <LBox
        position="absolute"
        bottom={arrowBottom}
        left={arrowLeft}
        pointerEvents="none"
        style={{ transform: [{ rotate: "60deg" }] }}
      >
        <CurvedArrow />
      </LBox>
    </LBox>
  );
};
